import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, MapPin, SlidersHorizontal, X } from 'lucide-react';
import { formatDate, getConditionColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', name: 'All Categories' },
  { id: 'electronics', name: 'Electronics' },
  { id: 'clothing', name: 'Clothing' },
  { id: 'furniture', name: 'Furniture' },
  { id: 'books', name: 'Books' },
  { id: 'sports', name: 'Sports' },
  { id: 'home-garden', name: 'Home & Garden' },
  { id: 'toys', name: 'Toys' },
  { id: 'other', name: 'Other' },
];

const conditions = [
  { id: 'all', name: 'Any Condition' },
  { id: 'new', name: 'New' },
  { id: 'like-new', name: 'Like New' },
  { id: 'good', name: 'Good' },
  { id: 'fair', name: 'Fair' },
  { id: 'poor', name: 'Poor' },
];

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || 'all',
    condition: searchParams.get('condition') || 'all',
    location: searchParams.get('location') || ''
  });

  useEffect(() => {
    fetchListings();
  }, [searchParams, user]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.condition && filters.condition !== 'all') params.append('condition', filters.condition);
      if (filters.location) params.append('location', filters.location);
      if (user?.id) params.append('exclude_user', user.id);
      
      const response = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category && filters.category !== 'all') params.set('category', filters.category);
    if (filters.condition && filters.condition !== 'all') params.set('condition', filters.condition);
    if (filters.location) params.set('location', filters.location);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: 'all', condition: 'all', location: '' });
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.category !== 'all' || filters.condition !== 'all' || filters.location;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F7] animate-fadeIn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Browse Items</h1>
          <p className="text-stone-600">Find items to trade in your area</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder="Search items..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                className="pl-10 h-12 rounded-xl"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-12 rounded-xl px-4"
                data-testid="filters-toggle"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button
                onClick={applyFilters}
                className="h-12 bg-[#008080] hover:bg-[#008080]/90 rounded-xl px-6"
                data-testid="apply-filters-btn"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-1 md:grid-cols-3 gap-4 animate-slideDown">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Category</label>
                <Select
                  value={filters.category}
                  onValueChange={(value) => handleFilterChange('category', value)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Condition</label>
                <Select
                  value={filters.condition}
                  onValueChange={(value) => handleFilterChange('condition', value)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((cond) => (
                      <SelectItem key={cond.id} value={cond.id}>{cond.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Location</label>
                <Input
                  placeholder="City name..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="h-12 rounded-xl"
                  data-testid="location-input"
                />
              </div>
            </div>
          )}

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-stone-100 flex flex-wrap gap-2 items-center">
              <span className="text-sm text-stone-500">Active filters:</span>
              {filters.search && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-stone-100 text-sm">
                  Search: {filters.search}
                </span>
              )}
              {filters.category !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-stone-100 text-sm">
                  {categories.find(c => c.id === filters.category)?.name}
                </span>
              )}
              {filters.condition !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-stone-100 text-sm">
                  {conditions.find(c => c.id === filters.condition)?.name}
                </span>
              )}
              {filters.location && (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-stone-100 text-sm">
                  <MapPin className="w-3 h-3 mr-1" />
                  {filters.location}
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-stone-500 hover:text-stone-700 flex items-center"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="empty-state bg-white rounded-2xl border border-stone-100">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No items found</h3>
            <p className="text-stone-600 mb-6">Try adjusting your filters or search terms</p>
            <Button variant="outline" onClick={clearFilters} className="rounded-full">
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-500 mb-4">{listings.length} items found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="listing-card group bg-white rounded-2xl overflow-hidden border border-stone-100"
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-stone-100">
                    {listing.images?.[0] ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400">
                        No Image
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getConditionColor(listing.condition)}`}>
                        {listing.condition}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-stone-900 mb-1 line-clamp-1 group-hover:text-[#008080] transition-colors">
                      {listing.title}
                    </h3>
                    <p className="text-sm text-stone-500 line-clamp-2 mb-3">{listing.description}</p>
                    <div className="flex items-center justify-between text-xs text-stone-400">
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {listing.location}
                      </div>
                      <span>{formatDate(listing.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
